import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import LazyImage from "./LazyImage";

const CountryCodeDropdown = React.memo(
  ({
    label,
    options = [],
    placeholder = "None",
    name,
    id,
    onChange,
    value,
    error,
    disabled = false,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
      if (value && options.length > 0) {
        const match = options.find((opt) => opt.id === value || opt.value === value);
        if (match) setSelected(match);
      }
    }, [value, options]);

    useEffect(() => {
    const handleClickOutside = (e) => {
        if ( !buttonRef.current?.contains(e.target) &&
      !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
        }
    };

    document.addEventListener("mouseup", handleClickOutside);
    return () => document.removeEventListener("mouseup", handleClickOutside);
    }, []);

    useEffect(() => {
      const updateDropdownPosition = () => {
        if (isOpen && buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();

          const isVisible =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

          if (!isVisible) {
            setIsOpen(false);
            return;
          }

          setDropdownPos({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      if (isOpen) {
        window.addEventListener("scroll", updateDropdownPosition, true);
        window.addEventListener("resize", updateDropdownPosition);
        updateDropdownPosition();
      }

      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }, [isOpen]);

    const handleSelect = (event, option) => {
        // console.log("===>>>",option);
      setSelected(option);
      setIsOpen(false);

      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          label,
          name,
          value: option.isoCode,
        },
      };
      
      onChange?.(syntheticEvent, option);
    };
    // console.log("isOpen ===>>",isOpen);
    return (
      <>
        <div id={id} name={name} className="relative ">
          {label && (
            <label htmlFor={name} className="block text-left font-medium text-gray-700 mb-1">
              {label}
            </label>
          )}
        <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center justify-between ${disabled && "bg-gray-100"} gap-2 w-[8rem] px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
            {options?.find(val => val?.isoCode === value) && (<LazyImage className="w-5" src={options?.find(val => val?.isoCode === value)?.flag} alt={options?.find(val => val?.isoCode === value)?.name} />)}
            <span className="truncate overflow-hidden whitespace-nowrap flex-1 text-left">
                { options?.find(val => val?.isoCode === value)?.name || placeholder}
            </span>
            <FaChevronDown className="text-sm text-blue-800 flex-shrink-0" />
        </button>
        {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
        </div>

        {isOpen &&
          createPortal(
            <ul
                ref={dropdownRef}
                className="z-50 min-w-16 max-w-54 bg-white border border-gray-300 rounded shadow-md max-h-60 overflow-auto text-sm"
                style={{ position: "absolute", top: `${dropdownPos.top + 5}px`, left: `${dropdownPos.left}px`, }}
            >
              {Array.isArray(options) && options.length > 0 ? (
                options.map((option) => (
                  <li
                    key={option.isoCode}
                    onClick={(e) => { e.stopPropagation(); handleSelect(e, option)}}
                    className=" flex items-center flex-nowrap gap-2 px-3 py-2 cursor-pointer hover:bg-lime-100 text-gray-700"
                  >
                    <LazyImage className="w-5" src={option?.flag} alt={option?.name} />
                    {`${option.name} ${option?.phoneCode}`}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-gray-700 text-center">No Options</li>
              )}
            </ul>, document.body)}
      </>
    );
  }
);


export default CountryCodeDropdown;